import { css } from "glamor";
import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import { Label, Icon } from "semantic-ui-react";
import { spacing, uiColors, styles, typographyColors } from "../library/theme";
import QuestionNavigationContainer from "./QuestionNavigationContainer";
import { InteractiveNavFooterContext } from "./interactive/InteractiveNavFooterContext";
import QuestionStemAccordion from "./QuestionStemAccordion";
import QuestionComments from "./QuestionComments";
import Link from "../library/Link";
import Button from "../library/Button";
import DifficultyBar from "../library/DifficultyBar";
import { getQuestionStemsByQuestionId } from "../../selectors/questionStemSelectors";
import {
  getAnswerForQuestionStem,
  getAnswerForLessonQuestionStem,
  getAllStemsAnswered,
} from "../../selectors/answerSelector";
import { makeDiscardChoice } from "../../selectors/choiceSelectors";
import { createMockAnswerV2 } from "../../processes/answerProccess";
import {
  selectChoice,
  toggleDiscardChoice,
} from "../../processes/choiceProcess";
import { goToIndex } from "../../processes/questionProccess";
import { goToLessonQuestionIndex } from "../../processes/lessonQuestionProcess";
import { getFlattenedLessonQuestions } from "../../selectors/lessonQuestionSelectors";
import { getCourseFromMock } from "../../selectors/courseSelectors";
import { getMock, makeIsLastAnswer } from "../../selectors/mockSelectors";
import { getMockPreferenceByName } from "../../selectors/mockPreferencesSelectors";
import { showAnswer } from "../../selectors/mockSelectors";
import { getFlattenedQuestionsFromMock } from "../../selectors/questionSelectors";
import TipTapViewer from "../library/TipTapViewer";

const commentClass = css({
  margin: `${spacing.m} 0`,
  paddingBottom: spacing.m,
  borderBottom: `1px solid ${uiColors.border}`,
});

const contextClass = css({
  paddingBottom: spacing.l,
  "> div, > p, * > p": {
    ...styles.bodyTextLarge,
    ...styles.bodyTextBold,
    fontWeight: "500",
    color: typographyColors.blackLetters,
  },
});

function QuestionV2(props) {
  const {
    question,
    mockId,
    currentStemPosition,
    dispatch,
    courseId,
    readOnly,
    showMockAnswer,
    lesson,
    mockPreferences,
    routePrefix,
    stems,
    isExam,
    isFinished,
    selectedChoices,
    flattenedQuestions,
    flattenedLessonQuestions,
    state,
    isLastAnswer,
    answered,
    allStemsAnswered,
    getIsDiscarded,
  } = props;

  const sortedStems = useMemo(() => {
    return stems ? [...stems].sort((a, b) => a.position - b.position) : [];
  }, [stems]);

  const getInitialActiveIndex = () => {
    if (!sortedStems || sortedStems.length === 0) return 0;
    if (sortedStems.length === 1) return 0;
    if (readOnly) return "all";
    if (currentStemPosition !== null && currentStemPosition !== undefined) {
      const stemIndex = sortedStems.findIndex(
        (stem) => stem.position === currentStemPosition
      );
      if (stemIndex !== -1) return stemIndex;
    }
    return 0;
  };

  const [activeAccordionIndex, setActiveAccordionIndex] = useState(
    getInitialActiveIndex
  );

  useEffect(() => {
    if (!sortedStems || sortedStems.length === 0) return;

    let newActiveIndex = 0;
    if (sortedStems.length === 1) {
      newActiveIndex = 0;
    } else if (readOnly) {
      newActiveIndex = "all";
    } else if (
      currentStemPosition !== null &&
      currentStemPosition !== undefined
    ) {
      const stemIndex = sortedStems.findIndex(
        (stem) => stem.position === currentStemPosition
      );
      if (stemIndex !== -1) {
        newActiveIndex = stemIndex;
      }
    }

    if (newActiveIndex !== activeAccordionIndex) {
      setActiveAccordionIndex(newActiveIndex);
    }
  }, [currentStemPosition, sortedStems, activeAccordionIndex, readOnly]);

  const questionCommentClass = css(
    commentClass,
    readOnly && {
      borderBottom: "none",
      paddingBottom: "0",
      marginBottom: "0",
    }
  );

  const hasComments =
    question?.vimeoUrl ||
    question?.scaleupUrl ||
    question?.commentVideoUrl ||
    question?.commentText ||
    question?.commentFileUrl;

  const showQuestionComments =
    hasComments &&
    (readOnly ||
      (answered && lesson && allStemsAnswered) ||
      (answered && showMockAnswer && mockPreferences?.showVideo && allStemsAnswered));

  const handleChoiceSelect = useCallback(
    (questionStemId, choiceId) => {
      if (isExam) {
        createMockAnswerV2(
          dispatch,
          courseId,
          mockId,
          question.id,
          questionStemId,
          choiceId,
          isLastAnswer
        );
      } else {
        selectChoice(dispatch, question.id, questionStemId, choiceId);
      }
    },
    [dispatch, courseId, mockId, question.id, isExam, isLastAnswer]
  );

  const handleAccordionToggle = useCallback(
    (stemIndex) => {
      const stem = sortedStems[stemIndex];

      if (!stem) return;

      if (readOnly) {
        return;
      }

      if (lesson && flattenedLessonQuestions) {
        const flattenedIndex = flattenedLessonQuestions?.findIndex(
          (item) =>
            item.question?.id === question.id && item.stem?.id === stem.id
        );

        if (flattenedIndex !== undefined && flattenedIndex !== -1) {
          goToLessonQuestionIndex(dispatch, flattenedIndex);
        }
      } else {
        const flattenedIndex = flattenedQuestions?.findIndex(
          (item) =>
            item.question?.id === question.id && item.stem?.id === stem.id
        );

        if (flattenedIndex !== undefined && flattenedIndex !== -1) {
          goToIndex(dispatch, flattenedIndex);
        }
      }

      setActiveAccordionIndex(stemIndex);
    },
    [
      sortedStems,
      question.id,
      flattenedQuestions,
      flattenedLessonQuestions,
      dispatch,
      readOnly,
      lesson,
    ]
  );

  const toggleDiscardChoiceHandler = useCallback(
    (choiceId, questionStemId) => {
      const isDiscarted = getIsDiscarded(choiceId, questionStemId);
      toggleDiscardChoice(
        { isDiscarted, choiceId, questionStemId, mockId },
        dispatch
      );
    },
    [dispatch, mockId, getIsDiscarded]
  );

  if (!question) return null;

  if (!stems || stems.length === 0) {
    return (
      <div>
        <div
          className={css({
            paddingBottom: spacing.l,
          })}
        >
          <p
            className={css({
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: spacing.m,
              "> strong": { marginRight: spacing.xxs },
            })}
          >
            <strong>CÓD. DA QUESTAO:</strong> {question.id}
            {question?.highlighted && (
              <Label
                color="yellow"
                size="tiny"
                style={{ marginLeft: spacing.s }}
              >
                <Icon name="star" />
                Questão recorrente
              </Label>
            )}
            {question?.calcRequired && (
              <Label color="red" size="tiny" style={{ marginLeft: spacing.s }}>
                <Icon name="exclamation" />
                Requer cálculo
              </Label>
            )}
            {question?.isFcc && (
              <Label color="blue" size="tiny" style={{ marginLeft: spacing.s }}>
                <Icon name="star" />
                FCC - Fundação Carlos Chagas
              </Label>
            )}
            {((answered && showMockAnswer && mockPreferences?.showDifficulty) ||
              readOnly) && (
              <DifficultyBar
                value={question?.errorsAverage}
                indicatorPopup={`Dificuldade: ${question?.errorsAverage}% erraram essa questão`}
              />
            )}
            {props.currentUser?.meta?.features?.canManageCourses && (
              <Link
                component={Button}
                size="mini"
                href={
                  question?.version === 2
                    ? `/manager/v2/questions/${question.id}/edit`
                    : `/manager/questions/${question.id}/edit`
                }
                target="_blank"
                style={{ marginLeft: spacing.s }}
              >
                Editar questão
              </Link>
            )}
          </p>
        </div>
        {question.context && (
          <TipTapViewer content={question.context} className={contextClass} />
        )}
        <p>Carregando enunciados...</p>
      </div>
    );
  }

  const navFooterEl = useContext(InteractiveNavFooterContext);

  // For interactive hybrid questions, the LMS pagination already routes each
  // stem to its own pagination entry — rendering the sibling stems as
  // collapsibles below the active one is redundant and confusing. Render only
  // the current stem in that case.
  const stemsToRender = useMemo(() => {
    if (question?.format === "interactive" && currentStemPosition != null) {
      const match = sortedStems.find((s) => s.position === currentStemPosition);
      return match ? [match] : sortedStems;
    }
    return sortedStems;
  }, [question?.format, currentStemPosition, sortedStems]);

  const hasSingleStem = stemsToRender.length === 1;

  return (
    <div>
      <div
        className={css({
          paddingBottom: spacing.l,
        })}
      >
        <p
          className={css({
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: spacing.m,
            "> strong": { marginRight: spacing.xxs },
          })}
        >
          <strong>CÓD. DA QUESTAO:</strong> {question.id}
          {question?.highlighted && (
            <Label color="yellow" size="tiny" style={{ marginLeft: spacing.s }}>
              <Icon name="star" />
              Questão recorrente
            </Label>
          )}
          {question?.calcRequired && (
            <Label color="red" size="tiny" style={{ marginLeft: spacing.s }}>
              <Icon name="exclamation" />
              Requer cálculo
            </Label>
          )}
          {question?.isFcc && (
            <Label color="blue" size="tiny" style={{ marginLeft: spacing.s }}>
              <Icon name="star" />
              FCC - Fundação Carlos Chagas
            </Label>
          )}
          {((props.answered &&
            showMockAnswer &&
            mockPreferences?.showDifficulty) ||
            readOnly) && (
            <DifficultyBar
              value={question?.errorsAverage}
              indicatorPopup={`Dificuldade: ${question?.errorsAverage}% erraram essa questão`}
            />
          )}
          {props.currentUser?.meta?.features?.canManageCourses && (
            <Link
              component={Button}
              size="mini"
              href={
                question?.version === 2
                  ? `/manager/v2/questions/${question.id}/edit`
                  : `/manager/questions/${question.id}/edit`
              }
              target="_blank"
              style={{ marginLeft: spacing.s }}
            >
              Editar questão
            </Link>
          )}
        </p>
      </div>

      {question.context && (
        <TipTapViewer content={question.context} className={contextClass} />
      )}

      {stemsToRender.map((stem, stemIndex) => {
        const selectedChoiceId =
          selectedChoices?.[question.id]?.[stem.id] || null;
        const currentMock = getMock(state);
        const currentMockId = currentMock?.id;

        const flattenedItem = flattenedQuestions?.find(
          (item) =>
            item.question?.id === question.id && item.stem?.id === stem.id
        );
        const stemDisplayIndex = flattenedItem?.displayIndex;

        const answer = lesson
          ? getAnswerForLessonQuestionStem(state, {
              questionId: question.id,
              questionStemId: stem.id,
              lessonId: lesson?.id,
            })
          : getAnswerForQuestionStem(state, {
              questionId: question.id,
              questionStemId: stem.id,
              mockId: currentMockId,
            });

        const allStems = state.entities.questionStems || {};
        const stemObj =
          allStems[stem.id] ||
          Object.values(allStems).find((s) => Number(s.id) === Number(stem.id));

        const stemToUse = stemObj || stem;

        let answeredChoice = null;
        if (answer && stemToUse && stemToUse.choices) {
          answeredChoice = stemToUse.choices.find(
            (c) => Number(c.id) === Number(answer.choiceId)
          );
        }

        const isAnswered = !!answer;

        const showPercentagePreference = getMockPreferenceByName(
          state,
          "showPercentage"
        );

        if (hasSingleStem) {
          return (
            <QuestionStemAccordion
              key={stem.id}
              stem={stemToUse}
              stemIndex={stemIndex}
              questionId={question.id}
              mockId={mockId}
              selectedChoiceId={selectedChoiceId}
              answeredChoice={answeredChoice}
              onChoiceSelect={handleChoiceSelect}
              readOnly={readOnly}
              showAnswer={
                showAnswer(state) &&
                getMockPreferenceByName(state, "showAnswerStatus")
              }
              isFinished={isFinished}
              isExam={isExam}
              active={true}
              onToggle={() => {}}
              isAnswered={isAnswered}
              renderWithoutAccordion={true}
              isDiscarted={getIsDiscarded}
              toggleDiscardChoiceHandler={toggleDiscardChoiceHandler}
              showPercentage={showPercentagePreference}
              displayIndex={stemDisplayIndex}
            />
          );
        }

        return (
          <QuestionStemAccordion
            key={stem.id}
            stem={stemToUse}
            stemIndex={stemIndex}
            questionId={question.id}
            mockId={mockId}
            selectedChoiceId={selectedChoiceId}
            answeredChoice={answeredChoice}
            onChoiceSelect={handleChoiceSelect}
            readOnly={readOnly}
            showAnswer={
              showAnswer(state) &&
              getMockPreferenceByName(state, "showAnswerStatus")
            }
            isFinished={isFinished}
            isExam={isExam}
            active={readOnly ? true : activeAccordionIndex === stemIndex}
            onToggle={() => handleAccordionToggle(stemIndex)}
            isAnswered={isAnswered}
            isDiscarted={getIsDiscarded}
            toggleDiscardChoiceHandler={toggleDiscardChoiceHandler}
            showPercentage={showPercentagePreference}
            displayIndex={stemDisplayIndex}
          />
        );
      })}

      {!readOnly && (() => {
        const nav = (
          <QuestionNavigationContainer
            lesson={lesson}
            routePrefix={routePrefix}
          />
        );
        return navFooterEl ? createPortal(nav, navFooterEl) : nav;
      })()}

      {showQuestionComments && (
        <div className={questionCommentClass}>
          <QuestionComments question={question} />
        </div>
      )}
    </div>
  );
}

function mapStateToProps(state, ownProps) {
  const { question, lesson } = ownProps;
  const stems = getQuestionStemsByQuestionId(state, question?.id);
  const mock = getMock(state);
  const isExam = !lesson && mock?.exam;
  const selectedChoices = lesson
    ? state.lesson.selectedChoices || {}
    : state.mock.selectedChoices || {};
  const flattenedQuestions = getFlattenedQuestionsFromMock(state);
  const flattenedLessonQuestions = lesson
    ? getFlattenedLessonQuestions(state)
    : null;
  const mockId = mock?.id;

  const getIsDiscarded = (choiceId, questionStemId) => {
    return makeDiscardChoice(state, choiceId, questionStemId);
  };

  let answered = null;
  if (question && stems && stems.length > 0) {
    const hasAnyAnswer = stems.some((stem) => {
      if (lesson) {
        const answer = getAnswerForLessonQuestionStem(state, {
          questionId: question.id,
          questionStemId: stem.id,
          lessonId: lesson?.id,
        });
        return !!answer;
      } else {
        const answer = getAnswerForQuestionStem(state, {
          questionId: question.id,
          questionStemId: stem.id,
          mockId: mockId,
        });
        return !!answer;
      }
    });

    if (hasAnyAnswer) {
      answered = { value: true };
    }
  }

  return {
    stems,
    isExam,
    isFinished: mock?.finishedAt,
    selectedChoices,
    courseId: getCourseFromMock(state)?.id,
    flattenedQuestions,
    flattenedLessonQuestions,
    state,
    isLastAnswer: makeIsLastAnswer(state),
    mockId,
    answered,
    allStemsAnswered: getAllStemsAnswered(state, {
      question,
      mockId,
      lessonId: lesson?.id,
    }),
    getIsDiscarded,
  };
}

export default connect(mapStateToProps)(QuestionV2);
